<template>
  <div class="anamnesis-container">
    <Breadcrumbs />

    <!-- Filtres avec boutons -->
    <AssetFilters
      v-model:active-filter="viewFilter"
      title="Agents"
      asset-label="agents"
      :stats="agentsStats"
      :is-loading="isLoading"
      @refresh="refreshAgents"
    />

    <div v-if="isLoading" class="loader">
      <div class="spinner"></div>
    </div>

    <template v-else>
      <AgentSubset
        :agents="filteredAgents"
        :show-actions="true"
        :show-edit-button="true"
        :show-delete-button="true"
        @create="openCreationModal"
        @edit="editAgent"
        @delete="handleDeleteAgent"
      />
    </template>

    <!-- Composant AgentCreator -->
    <AgentCreator 
      v-model:show="showCreatorModal"
      :user-id="userId"
      :mode="creatorMode"
      :initial-data="selectedAgent"
      @agent-created="handleAgentCreated"
      @agent-updated="handleAgentUpdated"
    />
    <Notification ref="notification" />
  </div>
</template>

<script setup lang="ts">
import { useAgents } from '@/composables/useAssets';
import { useAuthStore } from '@/stores/auth';
import type { NotificationType } from "@/types/notification";
import { computed, ref } from "vue";
import { agents } from "../../api/agents";
import type { AgentWithMemos } from "../../api/types/entities/agent/relations";
import type { Agent } from "../../api/types/entities/agent/types";
import Breadcrumbs from "../../components/Breadcrumbs.vue";
import Notification from "../../components/Notification.vue";
import AssetFilters from "../../components/UI/AssetFilters.vue";
import AgentCreator from "../../components/creators/AgentCreator.vue";
import AgentSubset from "../../components/subsets/AgentSubset.vue";
import { useBreadcrumbsStore } from "../../stores/breadcrumbs";

// Stores et composables
const authStore = useAuthStore();
const breadcrumbsStore = useBreadcrumbsStore();
const {
  filteredAssets: filteredAgents,
  isLoading,
  stats: agentsStats,
  viewFilter,
  refreshAssets: refreshAgents,
  addAsset: addAgent,
  updateAsset: updateAgent,
  removeAsset: removeAgent
} = useAgents({ autoLoad: true });

// État local
const notification = ref<InstanceType<typeof Notification> | null>(null);
const showCreatorModal = ref(false);
const creatorMode = ref<'create' | 'edit'>('create');
const selectedAgent = ref<Agent | undefined>(undefined);

// Computed
const userId = computed((): string => authStore.user?.uuid || '');

// Initialiser les breadcrumbs
breadcrumbsStore.updateBreadcrumbs([
  { name: 'Outils', path: '/dashboard' },
  { name: 'Mes Agents', path: '/myagents' }
]);

// Méthodes (applyFilter supprimé car le filtrage est automatique via le composable)

const openCreationModal = (): void => {
  selectedAgent.value = undefined;
  creatorMode.value = 'create';
  showCreatorModal.value = true;
};

const editAgent = (agent: AgentWithMemos): void => {
  selectedAgent.value = agent;
  creatorMode.value = 'edit';
  showCreatorModal.value = true;
};

const handleAgentCreated = (agent: Agent): void => {
  console.log('[MyAgents] handleAgentCreated appelé avec:', agent);
  // Convertir en AgentWithMemos avec un tableau de mémos vide
  const agentWithMemos = { ...agent, memos: [] };
  addAgent(agentWithMemos);
  console.log('[MyAgents] addAgent appelé');
  showNotification("Agent créé avec succès", "success");
};

const handleAgentUpdated = (agent: Agent): void => {
  // Convertir en AgentWithMemos en préservant les mémos existants
  const existingAgent = filteredAgents.value.find(a => a.uuid === agent.uuid);
  const agentWithMemos = { ...agent, memos: existingAgent?.memos || [] };
  updateAgent(agentWithMemos);
  showNotification("Agent mis à jour avec succès", "success");
};

const handleDeleteAgent = async (agent: AgentWithMemos): Promise<void> => {
  try {
    await agents.deleteAgent(agent.uuid);
    removeAgent(agent.uuid);
    showNotification("Agent supprimé avec succès", "success");
  } catch (error) {
    console.error("Erreur lors de la suppression de l'agent:", error);
    showNotification("Erreur lors de la suppression de l'agent", "error");
  }
};

const showNotification = (message: string, type: NotificationType): void => {
  if (notification.value) {
    notification.value.showNotification(message, type);
  }
};
</script>

<style>
.loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #3b82f6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style> 